import { IcPlus, IcTrash } from '@assets/icons/24x24';
import { Box, Center, HStack, Icon, IconButton, Separator, Stack, Text, VStack } from '@chakra-ui/react';
import { GPSCoordinatesList } from '@components/ui/coordinatesInput';
import { GPSCoordinatesRootProps } from '@components/ui/coordinatesInput/GPSCoordinates.slots';
import { FieldArray, getIn, useFormikContext } from 'formik';
import { Position } from 'geojson';
import { ForwardedRef, forwardRef } from 'react';

import { CoordinatesUnit, CoordinatesUnitEnum } from '@/types/commons/commons.types';

interface GPSCoordinatesListFieldProps<TValues, K extends keyof TValues & string> {
  ref: ForwardedRef<HTMLDivElement>;
  name: K;
  unit: CoordinatesUnit;
  pointHasTwoLevels: boolean;
  setUnit: (unit: CoordinatesUnit) => void;
  setPolygon: (field: string, value: string) => void;
  deleteCoordinate: (index: number) => void;
  addCoordinate: () => void;
  positionPicker?: boolean;
}

export function GPSCoordinatesListFieldContent<
  TValues extends Record<string, Position[] | undefined>,
  K extends keyof TValues & string,
>({
  ref,
  name,
  unit,
  pointHasTwoLevels,
  setUnit,
  setPolygon,
  deleteCoordinate,
  addCoordinate,
  positionPicker = true,
}: Readonly<GPSCoordinatesListFieldProps<TValues, K>>) {
  const { values } = useFormikContext<TValues>();
  const coordinates: Position[] = getIn(values, name) ?? [];

  const dmsWidth = pointHasTwoLevels ? '598px' : '582px';

  return (
    <GPSCoordinatesList.Root ref={ref} width={unit === CoordinatesUnitEnum.DECIMAL_DEGREES ? '558px' : dmsWidth}>
      <VStack gap={0} width="100%">
        <HStack width="100%" padding={2}>
          <Box width="100%" justifySelf="start">
            <GPSCoordinatesList.UnitSelector unit={unit} onUnitChange={(value) => setUnit(value)} />
          </Box>
          <Box justifySelf={{ base: 'end' }}>{positionPicker ? <GPSCoordinatesList.PositionPicker /> : null}</Box>
        </HStack>
        <Separator size="md" borderColor="neutral.800" />
        <FieldArray name={name}>
          {({ push, remove }) => (
            <Stack width="100%" gap={2} padding={2}>
              {coordinates.map((_, index) => (
                <HStack key={`coordinates-${index}`} gap={2}>
                  <HStack gap={1}>
                    <Center
                      boxSize="32px"
                      border="2px solid"
                      borderColor="neutral.white"
                      minWidth="24px"
                      paddingX="3px"
                    >
                      <Text textStyle="md" fontWeight="medium">
                        {index + 1}
                        {pointHasTwoLevels ? 'A' : ''}
                      </Text>
                    </Center>
                    {pointHasTwoLevels && (
                      <Center
                        boxSize="32px"
                        border="2px solid"
                        borderColor="neutral.white"
                        minWidth="24px"
                        paddingX="3px"
                      >
                        <Text textStyle="md" fontWeight="medium">
                          {index + 1}B
                        </Text>
                      </Center>
                    )}
                  </HStack>
                  <GPSCoordinatesList.Input
                    type="latitude"
                    name={`${name}[${index}][1]`}
                    unit={unit}
                    onPolygonChange={setPolygon}
                  />
                  <GPSCoordinatesList.Input
                    type="longitude"
                    name={`${name}[${index}][0]`}
                    unit={unit}
                    onPolygonChange={setPolygon}
                  />
                  <IconButton
                    variant="pick"
                    aria-label="delete"
                    onClick={() => {
                      remove(index);
                      deleteCoordinate(index);
                    }}
                  >
                    <Icon as={IcTrash} boxSize="24px" />
                  </IconButton>
                </HStack>
              ))}

              <IconButton
                variant="pick"
                aria-label="add-coordinate"
                boxSize="40px"
                marginLeft={pointHasTwoLevels ? 11 : 6}
                justifySelf="start"
                onClick={() => {
                  push([null, null]);
                  addCoordinate();
                }}
              >
                <Icon as={IcPlus} boxSize="24px" />
              </IconButton>
            </Stack>
          )}
        </FieldArray>
      </VStack>
    </GPSCoordinatesList.Root>
  );
}

export interface GPSCoordinatesListProps extends GPSCoordinatesRootProps {
  name: string;
  unit: CoordinatesUnit;
  setUnit: (unit: CoordinatesUnit) => void;
  setPolygon: (field: string, value: string) => void;
  deleteCoordinate: (index: number) => void;
  addCoordinate: () => void;
  positionPicker?: boolean;
  pointHasTwoLevels?: boolean;
}
export const GPSCoordinatesListField = forwardRef<HTMLDivElement, GPSCoordinatesListProps>(
  function GPSCoordinatesInput(props, ref) {
    const {
      name,
      positionPicker = false,
      pointHasTwoLevels = false,
      unit,
      setUnit,
      setPolygon,
      deleteCoordinate,
      addCoordinate,
      ...rest
    } = props;

    return (
      <GPSCoordinatesListFieldContent
        ref={ref}
        name={name}
        positionPicker={positionPicker}
        pointHasTwoLevels={pointHasTwoLevels}
        unit={unit}
        setUnit={setUnit}
        setPolygon={setPolygon}
        deleteCoordinate={deleteCoordinate}
        addCoordinate={addCoordinate}
        {...rest}
      />
    );
  },
);
